/**
 * Fleet Health — 7 actionable triage tiles + fixed 11-column device table.
 * Restructured from VendorDeviceHealth. FR-V10 to FR-V13
 */
import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { vendorFleetDevices } from '../../data/mockData';

type TileKey =
  | 'active_alarms' | 'critical_alarms' | 'plants_offline'
  | 'rms_offline'   | 'firmware_outdated' | 'certs_expiring'
  | 'certs_expired';

// Single source of truth — tile filter and count both use this function
function matchesTile(d: typeof vendorFleetDevices[0], key: TileKey): boolean {
  switch (key) {
    case 'active_alarms':     return d.alarms > 0;
    case 'critical_alarms':   return d.alarms > 2;
    case 'plants_offline':    return d.plantStatus === 'Offline';
    case 'rms_offline':       return d.rmsStatus === 'Disconnected';
    case 'firmware_outdated': return d.firmwareOutdated;
    case 'certs_expiring':    return d.daysRemaining > 0 && d.daysRemaining <= 30;
    case 'certs_expired':     return d.daysRemaining <= 0;
  }
}

// Counts derived from actual data — always in sync with table filter
function countForKey(key: TileKey): number {
  return vendorFleetDevices.filter(d => matchesTile(d, key)).length;
}

// Default view: devices with at least one issue across any tile
const hasAnyIssue = (d: typeof vendorFleetDevices[0]) =>
  (['active_alarms', 'critical_alarms', 'plants_offline', 'rms_offline',
    'firmware_outdated', 'certs_expiring', 'certs_expired'] as TileKey[])
    .some(k => matchesTile(d, k));

interface TileDef {
  key: TileKey;
  label: string;
  count: number;
  textCls: string;
  bgCls: string;
  borderCls: string;
}

const TILES: TileDef[] = [
  { key: 'active_alarms',     label: 'Active Alarms',     count: countForKey('active_alarms'),     textCls: 'text-slate-700',  bgCls: 'bg-slate-50',  borderCls: 'border-slate-500'  },
  { key: 'critical_alarms',   label: 'Critical Alarms',   count: countForKey('critical_alarms'),   textCls: 'text-red-600',    bgCls: 'bg-red-50',    borderCls: 'border-red-500'    },
  { key: 'plants_offline',    label: 'Plants Offline',    count: countForKey('plants_offline'),    textCls: 'text-rose-600',   bgCls: 'bg-rose-50',   borderCls: 'border-rose-500'   },
  { key: 'rms_offline',       label: 'RMS Offline',       count: countForKey('rms_offline'),       textCls: 'text-pink-600',   bgCls: 'bg-pink-50',   borderCls: 'border-pink-500'   },
  { key: 'firmware_outdated', label: 'Firmware Outdated', count: countForKey('firmware_outdated'), textCls: 'text-amber-600',  bgCls: 'bg-amber-50',  borderCls: 'border-amber-500'  },
  { key: 'certs_expiring',    label: 'Certs Expiring',    count: countForKey('certs_expiring'),    textCls: 'text-purple-600', bgCls: 'bg-purple-50', borderCls: 'border-purple-500' },
  { key: 'certs_expired',     label: 'Certs Expired',     count: countForKey('certs_expired'),     textCls: 'text-red-900',    bgCls: 'bg-red-100',   borderCls: 'border-red-800'    },
];

const plantStatusCls: Record<string, string> = {
  Online:  'bg-green-100 text-green-700',
  Offline: 'bg-gray-200 text-gray-600',
  Fault:   'bg-red-100 text-red-700',
};

function certDaysCls(days: number): string {
  if (days <= 0)  return 'text-red-600 font-semibold';
  if (days <= 30) return 'text-amber-600 font-semibold';
  return 'text-text-secondary';
}

function exportCSV(rows: typeof vendorFleetDevices, tileLabel?: string) {
  const headers = 'IMEI,Plant ID,Consumer,State,Plant Status,RMS Status,Firmware,Cert Expiry,Days Remaining,Last Seen,Alarms';
  const lines = rows.map(r =>
    [r.imei, r.plantId, r.consumer, r.state, r.plantStatus, r.rmsStatus,
     r.firmwareVersion, r.certExpiry, r.daysRemaining, r.lastSeen, r.alarms].join(',')
  );
  const csv = [headers, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `fleet-health${tileLabel ? `-${tileLabel.toLowerCase().replace(/\s+/g, '-')}` : ''}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function VendorFleetHealth() {
  const [activeTile, setActiveTile] = useState<TileKey | null>(null);

  const visibleRows = activeTile
    ? vendorFleetDevices.filter(d => matchesTile(d, activeTile))
    : vendorFleetDevices.filter(hasAnyIssue);

  const activeTileDef = TILES.find(t => t.key === activeTile);

  const tableHeader = activeTileDef
    ? `${activeTileDef.label} — ${visibleRows.length} device${visibleRows.length !== 1 ? 's' : ''}`
    : `Devices with Issues — ${visibleRows.length}`;

  const handleTileClick = (key: TileKey) => {
    setActiveTile(prev => prev === key ? null : key);
  };

  return (
    <div className="p-6 space-y-5">

      {/* ── 7 triage tiles ── */}
      <div className="flex gap-3">
        {TILES.map(tile => {
          const isActive = activeTile === tile.key;
          return (
            <button
              key={tile.key}
              onClick={() => handleTileClick(tile.key)}
              className={`
                flex-1 rounded-card border-2 p-3 text-left transition-all
                ${isActive
                  ? `${tile.bgCls} ${tile.borderCls}`
                  : `${tile.bgCls} border-transparent hover:${tile.borderCls}`
                }
              `}
            >
              <p className={`text-xl font-bold ${tile.textCls}`}>{tile.count}</p>
              <p className={`text-xs mt-0.5 font-medium ${tile.textCls}`}>{tile.label}</p>
            </button>
          );
        })}
      </div>
      {activeTile && (
        <button
          onClick={() => setActiveTile(null)}
          className="text-xs text-accent hover:underline"
        >
          Clear filter
        </button>
      )}

      {/* ── Device table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <p className="text-xs font-semibold text-text-primary">{tableHeader}</p>
          <button
            onClick={() => exportCSV(visibleRows, activeTileDef?.label)}
            className="flex items-center gap-1.5 text-xs text-text-tertiary hover:text-accent transition-colors px-2.5 py-1.5 rounded border border-border hover:border-accent"
          >
            <Download size={12} />
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Device IMEI</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Plant ID</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Consumer</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">State</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Plant Status</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">RMS Status</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Firmware</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Cert Expiry</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Days Left</th>
                <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Last Seen</th>
                <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Alarms</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(d => (
                <tr key={d.imei} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                  <td className="py-2.5 px-3 font-mono text-text-secondary text-xs">{d.imei}</td>
                  <td className="py-2.5 px-3 font-mono text-accent hover:underline cursor-pointer">{d.plantId}</td>
                  <td className="py-2.5 px-3 text-text-secondary">{d.consumer}</td>
                  <td className="py-2.5 px-3 text-text-tertiary">{d.state}</td>
                  <td className="py-2.5 px-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-pill text-xs font-medium ${plantStatusCls[d.plantStatus]}`}>
                      {d.plantStatus}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-medium ${d.rmsStatus === 'Connected' ? 'text-green-600' : 'text-red-600'}`}>
                      {d.rmsStatus}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`font-mono ${d.firmwareOutdated ? 'text-amber-600' : 'text-text-primary'}`}>
                      v{d.firmwareVersion}
                    </span>
                    {d.firmwareOutdated && (
                      <span className="ml-1.5 text-amber-600 font-medium" style={{ fontSize: 10 }}>Outdated</span>
                    )}
                  </td>
                  <td className={`py-2.5 px-3 ${certDaysCls(d.daysRemaining)}`}>
                    {d.certExpiry}
                    {d.daysRemaining <= 0 && (
                      <span className="ml-1 text-red-600 font-medium" style={{ fontSize: 10 }}>(EXPIRED)</span>
                    )}
                  </td>
                  <td className={`py-2.5 px-3 text-right ${certDaysCls(d.daysRemaining)}`}>
                    {d.daysRemaining <= 0
                      ? `${Math.abs(d.daysRemaining)}d overdue`
                      : `${d.daysRemaining}d`
                    }
                  </td>
                  <td className="py-2.5 px-3 text-text-tertiary">{d.lastSeen}</td>
                  <td className={`py-2.5 px-3 text-right ${d.alarms > 0 ? 'text-red-600 font-semibold' : 'text-text-tertiary'}`}>
                    {d.alarms > 0 ? d.alarms : '—'}
                  </td>
                </tr>
              ))}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-8 text-center text-xs text-text-tertiary">
                    No devices match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
